import type { ReactNode } from 'react';
import {
  Menu,
  Popover,
  type MenuProps,
  type PopoverOrigin,
} from '@mui/material';
import { designTokens } from '../tokens';

export type AnchoredOverlayPlacement = 'bottom-start' | 'bottom-end';

const origins: Record<AnchoredOverlayPlacement, { anchor: PopoverOrigin; transform: PopoverOrigin }> = {
  'bottom-start': {
    anchor: { vertical: 'bottom', horizontal: 'left' },
    transform: { vertical: 'top', horizontal: 'left' },
  },
  'bottom-end': {
    anchor: { vertical: 'bottom', horizontal: 'right' },
    transform: { vertical: 'top', horizontal: 'right' },
  },
};

export type AnchoredMenuProps = {
  id?: string;
  anchorEl: HTMLElement | null;
  open?: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: AnchoredOverlayPlacement;
  ariaLabel?: string;
  autoFocus?: MenuProps['autoFocus'];
};

/** Trigger의 aria-controls와 id를 연결하고, trigger 바로 아래에 배치하는 action menu입니다. */
export function AnchoredMenu({
  id,
  anchorEl,
  open = Boolean(anchorEl),
  onClose,
  children,
  placement = 'bottom-start',
  ariaLabel,
  autoFocus,
}: AnchoredMenuProps) {
  const origin = origins[placement];
  return (
    <Menu
      id={id}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={origin.anchor}
      transformOrigin={{ ...origin.transform, vertical: -designTokens.layout.menuAnchorGap }}
      autoFocus={autoFocus}
      slotProps={{ list: { 'aria-label': ariaLabel } }}
    >
      {children}
    </Menu>
  );
}

export type AnchoredPopoverSize = 'compact' | 'standard';
export type AnchoredPopoverPadding = 'none' | 'standard';

export type AnchoredPopoverProps = {
  id?: string;
  anchorEl: HTMLElement | null;
  open?: boolean;
  onClose: () => void;
  children: ReactNode;
  placement?: AnchoredOverlayPlacement;
  size?: AnchoredPopoverSize;
  padding?: AnchoredPopoverPadding;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
};

/**
 * 작은 편집기와 picker를 위한 anchored dialog입니다. 반응형 폭, viewport 최대 높이와 dialog 의미를 소유합니다.
 */
export function AnchoredPopover({
  id,
  anchorEl,
  open = Boolean(anchorEl),
  onClose,
  children,
  placement = 'bottom-start',
  size = 'compact',
  padding = 'none',
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
}: AnchoredPopoverProps) {
  const origin = origins[placement];
  const width = size === 'standard'
    ? designTokens.layout.anchoredPopoverStandardWidth
    : designTokens.layout.anchoredPopoverCompactWidth;
  const inlineInset = size === 'standard'
    ? designTokens.layout.anchoredPopoverStandardInlineInset
    : designTokens.layout.anchoredPopoverCompactInlineInset;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={origin.anchor}
      transformOrigin={{ ...origin.transform, vertical: -designTokens.layout.popupAnchorGap }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': ariaLabel,
          'aria-labelledby': ariaLabelledBy,
          'aria-describedby': ariaDescribedBy,
          sx: {
            width: { xs: `calc(100vw - ${inlineInset * 2}px)`, sm: width },
            maxWidth: `calc(100vw - ${designTokens.layout.popupViewportInset * 2}px)`,
            maxHeight: `min(${designTokens.layout.anchoredPopoverMaxHeight}px, calc(100dvh - ${designTokens.layout.anchoredPopoverBlockInset * 2}px))`,
            p: padding === 'standard' ? {
              xs: `${designTokens.layout.popupContentPaddingCompact}px`,
              sm: `${designTokens.layout.popupContentPadding}px`,
            } : 0,
          },
        },
      }}
    >
      {children}
    </Popover>
  );
}
